/**
 * Uniform-grid spatial hash for broad-phase neighbour queries.
 *
 * Eat checks would be O(n^2) if every entity tested every other. The grid lets
 * us only test entities in nearby cells, so performance holds as the arena
 * fills up. Cell size is tunable (config.spatial.cellSize).
 */
import type { Entity } from './types';

export class SpatialHash {
  private readonly cellSize: number;
  private readonly cells = new Map<number, Entity[]>();

  constructor(cellSize: number) {
    this.cellSize = cellSize;
  }

  private key(cx: number, cy: number): number {
    // Pack two signed cell coords into one number key. The 0x8000 bias keeps
    // negative coords distinct; arenas stay well within +/-32k cells.
    return ((cx + 0x8000) << 16) | (cy + 0x8000);
  }

  clear(): void {
    this.cells.clear();
  }

  insert(entity: Entity): void {
    const cx = Math.floor(entity.pos.x / this.cellSize);
    const cy = Math.floor(entity.pos.y / this.cellSize);
    const k = this.key(cx, cy);
    const bucket = this.cells.get(k);
    if (bucket) bucket.push(entity);
    else this.cells.set(k, [entity]);
  }

  /** Rebuild the grid from a fresh list of (alive) entities. */
  rebuild(entities: Entity[]): void {
    this.clear();
    for (const e of entities) this.insert(e);
  }

  /**
   * Collect every entity whose cell is within `radius` world units of (x, y).
   * Results may include entities slightly outside `radius`; callers do the
   * precise distance test. Results exclude nothing by id — caller filters self.
   */
  queryRadius(x: number, y: number, radius: number, out: Entity[] = []): Entity[] {
    out.length = 0;
    const minCx = Math.floor((x - radius) / this.cellSize);
    const maxCx = Math.floor((x + radius) / this.cellSize);
    const minCy = Math.floor((y - radius) / this.cellSize);
    const maxCy = Math.floor((y + radius) / this.cellSize);
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        const bucket = this.cells.get(this.key(cx, cy));
        if (bucket) {
          for (let i = 0; i < bucket.length; i++) out.push(bucket[i]);
        }
      }
    }
    return out;
  }
}
